import type { FC } from 'react'
import type { JobCard as JobCardData } from '@/types'

import Link from 'next/link'
import { Briefcase, Clock, MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { formatSalary, isFresh, relativeAge } from '@/lib/format'
import { CompanyLogo } from '@/components/generals/company-logo'
import { useSite } from '@/components/generals/site-context'
import { SaveJobButton } from '@/components/jobs/save-job-button'
import { MatchBadge } from '@/components/jobs/match-badge'
import { Badge } from '@/components/ui/badge'

import { levelLabel, workTypeLabel } from '@/constants/taxonomy'

interface JobCardProps {
    job: JobCardData
    compact?: boolean
    className?: string
    onSavedChange?: (saved: boolean) => void
}

export const JobCard: FC<JobCardProps> = ({ job, compact, className, onSavedChange }) => {
    const { now } = useSite()
    const salary = formatSalary(job.salaryMin, job.salaryMax, job.salaryCurrency, job.salaryPeriod)
    const fresh = isFresh(job.postedAt, now)
    const workType = workTypeLabel(job.workType)
    const level = levelLabel(job.experienceLevel)

    return (
        <article
            className={cn(
                'group relative flex gap-4 rounded-2xl border border-border/80 bg-card p-4 shadow-card transition-all hover:border-primary/30 hover:shadow-lift sm:p-5',
                job.status !== 'open' && 'opacity-70',
                className
            )}
        >
            <CompanyLogo name={job.company.name} logo={job.company.logo} logoAlt={job.company.logoAlt} />

            <div className='flex min-w-0 flex-1 flex-col gap-2'>
                <div className='flex items-start justify-between gap-3'>
                    <div className='min-w-0'>
                        <h3 className='text-[0.98rem] leading-snug font-semibold text-foreground'>
                            <Link
                                href={`/jobs/${job.slug}`}
                                className='outline-none after:absolute after:inset-0 after:rounded-2xl focus-visible:after:ring-3 focus-visible:after:ring-ring/40 group-hover:text-primary'
                            >
                                <span data-mdlfy-cms={cms('jobs', job.id, 'title')}>{job.title}</span>
                            </Link>
                        </h3>
                        <p className='mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground'>
                            <span className='font-medium text-foreground/80' data-mdlfy-cms={cms('companies', job.company.id, 'name')}>{job.company.name}</span>
                            {job.location && (
                                <span className='inline-flex min-w-0 items-center gap-1'>
                                    <MapPin className='size-3.5 shrink-0' />
                                    <span className='truncate' data-mdlfy-cms={cms('jobs', job.id, 'location')}>{job.location}</span>
                                </span>
                            )}
                        </p>
                    </div>

                    <div className='flex shrink-0 items-center gap-1'>
                        {job.match !== null && <MatchBadge score={job.match} className='hidden sm:inline-flex' />}
                        <SaveJobButton jobId={job.id} saved={job.saved} onChange={onSavedChange} />
                    </div>
                </div>

                <div className='flex flex-wrap items-center gap-1.5'>
                    {job.status !== 'open' && <Badge variant='destructive'>No longer open</Badge>}
                    {fresh && job.status === 'open' && <Badge variant='success'>New</Badge>}
                    {workType && <Badge variant='soft'>{workType}</Badge>}
                    {level && <Badge variant='outline'>{level}</Badge>}
                    {job.match !== null && <MatchBadge score={job.match} className='sm:hidden' />}
                </div>

                {!compact && job.skills.length > 0 && (
                    <ul className='flex flex-wrap gap-1.5' aria-label='Skills'>
                        {job.skills.slice(0, 5).map(skill => (
                            <li key={skill} className='rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'>
                                {skill}
                            </li>
                        ))}
                    </ul>
                )}

                <div className='mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
                    {salary && (
                        <span className='inline-flex items-center gap-1 font-semibold text-foreground'>
                            <Briefcase className='size-3.5 text-muted-foreground' />
                            {salary}
                        </span>
                    )}
                    <span className='inline-flex items-center gap-1'>
                        <Clock className='size-3.5' />
                        {relativeAge(job.postedAt, now) || 'Recently'}
                    </span>
                </div>
            </div>
        </article>
    )
}

export const JobCardSkeleton: FC = () => (
    <div className='flex gap-4 rounded-2xl border border-border/80 bg-card p-5'>
        <div className='size-11 animate-pulse rounded-xl bg-muted' />
        <div className='flex flex-1 flex-col gap-3'>
            <div className='h-4 w-2/3 animate-pulse rounded bg-muted' />
            <div className='h-3 w-1/3 animate-pulse rounded bg-muted' />
            <div className='flex gap-2'>
                <div className='h-5 w-16 animate-pulse rounded-full bg-muted' />
                <div className='h-5 w-20 animate-pulse rounded-full bg-muted' />
            </div>
        </div>
    </div>
)
